import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Loader, EmptyState, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { useAuth } from "../lib/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const POLICY_COLORS = {
  allowed: { bg: "var(--success-soft)", color: "var(--success)" },
  conditional: { bg: "var(--warn-soft)", color: "var(--warn)" },
  restricted: { bg: "var(--danger-soft)", color: "var(--danger)" },
  notified_negative: { bg: "var(--danger-soft)", color: "var(--danger)" },
};
const RATING_COLORS = {
  low: { bg: "var(--success-soft)", color: "var(--success)" },
  medium: { bg: "var(--warn-soft)", color: "var(--warn)" },
  high: { bg: "var(--danger-soft)", color: "var(--danger)" },
  critical: { bg: "var(--danger-soft)", color: "var(--danger)" },
};

function Pill({ label, mapObj }) {
  const t = mapObj?.[label] || { bg: "var(--bg-2)", color: "var(--muted)" };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-[3px] text-[10.5px] font-medium rounded-md"
      style={{ background: t.bg, color: t.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
      {label?.replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ label, value, tone = "primary", testId, hint }) {
  const c = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
          : tone === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: c }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

const emptyXfer = {
  destination_country: "", foreign_entity: "", data_categories: [],
  purpose: "", legal_basis: "", approval_status: "pending", risk_score: 0,
};

export default function CrossBorderTransfers() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "org_admin", "dpo"].includes(user?.role);
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [policy, setPolicy] = useState([]);
  const [loading, setLoading] = useState(false);
  const [xModal, setXModal] = useState(false);
  const [xForm, setXForm] = useState(emptyXfer);
  const [editing, setEditing] = useState(null);
  const [pModal, setPModal] = useState(false);
  const [pForm, setPForm] = useState({ country: "", policy: "allowed", reason: "" });
  const [sccModal, setSccModal] = useState(null);
  const [sccContent, setSccContent] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [d, t, p] = await Promise.all([
        api.get("/cross-border/dashboard"),
        api.get("/cross-border-transfers"),
        api.get("/cross-border/country-policy"),
      ]);
      setDash(d.data);
      setTransfers(t.data.items || t.data || []);
      setPolicy(p.data.items || []);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveTransfer = async () => {
    try {
      const payload = {
        ...xForm,
        data_categories: typeof xForm.data_categories === "string"
          ? xForm.data_categories.split(",").map(s => s.trim()).filter(Boolean)
          : xForm.data_categories,
      };
      if (editing) {
        await api.put(`/cross-border-transfers/${editing.id}`, payload);
        toast.success("Transfer updated");
      } else {
        await api.post("/cross-border-transfers", payload);
        toast.success("Transfer added");
      }
      setXModal(false); setEditing(null); setXForm(emptyXfer);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const evaluate = async (t) => {
    try {
      const { data } = await api.post(`/cross-border/transfers/${t.id}/evaluate`);
      toast.success(`Risk: ${data.risk_rating} (${data.risk_score})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Evaluation failed"); }
  };

  const generateSCC = async (t) => {
    setSccModal(t); setSccContent("");
    toast.info("Generating SCC via AI...");
    try {
      const { data } = await api.post(`/cross-border/transfers/${t.id}/generate-scc`);
      setSccContent(data.content);
      toast.success("SCC generated");
    } catch { toast.error("Failed"); }
  };

  const savePolicy = async () => {
    try {
      await api.post("/cross-border/country-policy", pForm);
      toast.success("Country policy saved");
      setPModal(false); setPForm({ country: "", policy: "allowed", reason: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const delPolicy = async (country) => {
    if (!window.confirm(`Remove policy for ${country}?`)) return;
    try {
      await api.delete(`/cross-border/country-policy/${encodeURIComponent(country)}`);
      toast.success("Removed"); await load();
    } catch { toast.error("Failed"); }
  };

  const openEdit = (t) => { setEditing(t); setXForm({ ...emptyXfer, ...t }); setXModal(true); };
  const openNew = () => { setEditing(null); setXForm(emptyXfer); setXModal(true); };

  if (loading && !dash) return <Loader />;

  return (
    <div>
      <PageHeader
        title="Cross-Border Transfers"
        kicker="DPDP Sec. 16 Transfer Control"
        description="Country policy engine, risk-scored transfer register, and AI-drafted Standard Contractual Clauses."
        testId="cb-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={load} data-testid="cb-refresh">
              <Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Btn>
            {isAdmin && tab === "register" && (
              <Btn onClick={openNew} data-testid="cb-new-transfer"><Icons.Plus className="w-3.5 h-3.5" /> New transfer</Btn>
            )}
            {isAdmin && tab === "policy" && (
              <Btn onClick={() => setPModal(true)} data-testid="cb-new-policy"><Icons.Plus className="w-3.5 h-3.5" /> Add country</Btn>
            )}
          </>
        }
      />

      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {[
          { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
          { key: "register", label: "Transfer register", icon: "Table" },
          { key: "policy", label: "Country policy", icon: "Globe2" },
        ].map((t) => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button key={t.key} data-testid={`cb-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{
                background: tab === t.key ? "var(--primary-soft)" : "transparent",
                color: tab === t.key ? "var(--primary)" : "var(--muted)",
              }}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && dash && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard label="Total transfers" value={dash.total_transfers} testId="stat-total" />
            <StatCard label="Critical risk" value={dash.by_rating?.critical || 0} tone="danger" testId="stat-critical" />
            <StatCard label="High risk" value={dash.by_rating?.high || 0} tone="danger" testId="stat-high" />
            <StatCard label="Unevaluated" value={dash.unevaluated || 0} tone="warn" testId="stat-unevaluated" />
            <StatCard label="Without SCC" value={dash.without_scc || 0} tone="warn" testId="stat-nosc" hint="Transfers missing signed clauses" />
            <StatCard label="Countries: allowed" value={dash.country_policy_breakdown?.allowed || 0} tone="success" testId="stat-allowed" />
            <StatCard label="Countries: conditional" value={dash.country_policy_breakdown?.conditional || 0} tone="warn" testId="stat-conditional" />
            <StatCard label="Countries: restricted" value={dash.country_policy_breakdown?.restricted || 0} tone="danger" testId="stat-restricted" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Top destinations</div>
              {(dash.by_country || []).length === 0 ? (
                <div className="text-[12px] muted">No transfers yet.</div>
              ) : (
                <div className="space-y-2" data-testid="by-country-list">
                  {dash.by_country.slice(0, 10).map(c => (
                    <div key={c.country} className="flex items-center gap-2">
                      <Icons.MapPin className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} />
                      <div className="flex-1 text-[12px] ink">{c.country}</div>
                      <div className="text-[12px] muted">{c.count}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
              <div className="text-[13px] font-semibold ink mb-3">Approval status</div>
              <div className="space-y-2">
                {Object.entries(dash.by_approval || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2">
                    <div className="flex-1 text-[12px] ink">{k}</div>
                    <div className="text-[12px] muted">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "register" && (
        transfers.length === 0 ? (
          <EmptyState title="No cross-border transfers registered" hint="Register any transfer of personal data outside India."
            action={isAdmin && <Btn onClick={openNew}><Icons.Plus className="w-4 h-4" /> New transfer</Btn>} />
        ) : (
          <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
            <table className="w-full text-sm">
              <thead style={{ background: "var(--bg-2)" }}>
                <tr>
                  {["Destination", "Entity", "Purpose", "Risk", "Approval", "SCC", "Actions"].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="transfers-tbody">
                {transfers.map(t => (
                  <tr key={t.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`xfer-${t.id}`}>
                    <td className="px-4 py-2 ink text-[12px]">{t.destination_country}</td>
                    <td className="px-4 py-2 ink text-[12px]">{t.foreign_entity}</td>
                    <td className="px-4 py-2 muted text-[11.5px]">{t.purpose}</td>
                    <td className="px-4 py-2">
                      {t.risk_rating ? <Pill label={t.risk_rating} mapObj={RATING_COLORS} /> : <span className="text-[10.5px] muted">not evaluated</span>}
                      {t.risk_score !== undefined && <div className="text-[10.5px] muted mt-1">Score: {t.risk_score}</div>}
                    </td>
                    <td className="px-4 py-2 text-[11.5px] muted">{t.approval_status}</td>
                    <td className="px-4 py-2 text-[11.5px]">
                      {t.scc_document_id ? (
                        <span className="inline-flex items-center gap-1" style={{ color: "var(--success)" }}>
                          <Icons.FileCheck className="w-3.5 h-3.5" /> Signed
                        </span>
                      ) : <span className="muted">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button onClick={() => evaluate(t)} className="text-[11px] font-medium mr-2 hover:underline" style={{ color: "var(--primary)" }} data-testid={`xfer-eval-${t.id}`}>
                        Evaluate
                      </button>
                      <button onClick={() => generateSCC(t)} className="text-[11px] font-medium mr-2 hover:underline" style={{ color: "var(--primary)" }} data-testid={`xfer-scc-${t.id}`}>
                        Generate SCC
                      </button>
                      {isAdmin && (
                        <button onClick={() => openEdit(t)} className="text-[11px] font-medium hover:underline muted" data-testid={`xfer-edit-${t.id}`}>
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "policy" && (
        <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
          <table className="w-full text-sm">
            <thead style={{ background: "var(--bg-2)" }}>
              <tr>
                {["Country", "Policy", "Reason", "Actions"].map(h => (
                  <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="policy-tbody">
              {policy.map(p => (
                <tr key={p.country} style={{ borderTop: "1px solid var(--border)" }}>
                  <td className="px-4 py-2 ink text-[12.5px] font-medium">{p.country}</td>
                  <td className="px-4 py-2"><Pill label={p.policy} mapObj={POLICY_COLORS} /></td>
                  <td className="px-4 py-2 muted text-[11.5px]">{p.reason || "—"}</td>
                  <td className="px-4 py-2 text-right">
                    {isAdmin && (
                      <>
                        <button onClick={() => { setPForm({ country: p.country, policy: p.policy, reason: p.reason || "" }); setPModal(true); }}
                          className="text-[11px] font-medium mr-2 hover:underline" style={{ color: "var(--primary)" }}>Edit</button>
                        <button onClick={() => delPolicy(p.country)} className="text-[11px] font-medium hover:underline" style={{ color: "var(--danger)" }}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Transfer modal */}
      <Dialog open={xModal} onOpenChange={setXModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit transfer" : "New cross-border transfer"}</DialogTitle>
            <DialogDescription>Register a transfer of personal data outside India (DPDP Sec. 16).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[62vh] overflow-y-auto">
            <Field label="Destination country" required><TextInput data-testid="xfer-country" value={xForm.destination_country} onChange={(e) => setXForm({ ...xForm, destination_country: e.target.value })} /></Field>
            <Field label="Foreign entity" required><TextInput data-testid="xfer-entity" value={xForm.foreign_entity} onChange={(e) => setXForm({ ...xForm, foreign_entity: e.target.value })} /></Field>
            <Field label="Purpose" required className="col-span-2"><TextInput data-testid="xfer-purpose" value={xForm.purpose} onChange={(e) => setXForm({ ...xForm, purpose: e.target.value })} /></Field>
            <Field label="Data categories (comma-sep)" className="col-span-2"><TextInput data-testid="xfer-cats" value={Array.isArray(xForm.data_categories) ? xForm.data_categories.join(", ") : xForm.data_categories} onChange={(e) => setXForm({ ...xForm, data_categories: e.target.value })} /></Field>
            <Field label="Legal basis" className="col-span-2"><TextArea data-testid="xfer-legal" value={xForm.legal_basis} onChange={(e) => setXForm({ ...xForm, legal_basis: e.target.value })} /></Field>
            <Field label="Approval status">
              <SelectInput data-testid="xfer-approval" value={xForm.approval_status} onChange={(e) => setXForm({ ...xForm, approval_status: e.target.value })}>
                {["pending", "approved", "rejected"].map(s => <option key={s} value={s}>{s}</option>)}
              </SelectInput>
            </Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setXModal(false)}>Cancel</Btn>
            <Btn onClick={saveTransfer} disabled={!xForm.destination_country || !xForm.foreign_entity} data-testid="xfer-save">
              {editing ? "Save" : "Create"}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Country policy modal */}
      <Dialog open={pModal} onOpenChange={setPModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Country transfer policy</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Field label="Country" required><TextInput data-testid="pol-country" value={pForm.country} onChange={(e) => setPForm({ ...pForm, country: e.target.value })} /></Field>
            <Field label="Policy" required>
              <SelectInput data-testid="pol-policy" value={pForm.policy} onChange={(e) => setPForm({ ...pForm, policy: e.target.value })}>
                <option value="allowed">Allowed (adequate framework)</option>
                <option value="conditional">Conditional (SCC required)</option>
                <option value="restricted">Restricted (avoid transfers)</option>
                <option value="notified_negative">Notified negative (blocked)</option>
              </SelectInput>
            </Field>
            <Field label="Reason"><TextArea data-testid="pol-reason" value={pForm.reason} onChange={(e) => setPForm({ ...pForm, reason: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setPModal(false)}>Cancel</Btn>
            <Btn onClick={savePolicy} disabled={!pForm.country} data-testid="pol-save">Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SCC viewer */}
      <Dialog open={!!sccModal} onOpenChange={(v) => !v && setSccModal(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Standard Contractual Clause — {sccModal?.destination_country} — {sccModal?.foreign_entity}</DialogTitle>
            <DialogDescription>AI-drafted SCC saved to Document Repository as a draft.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[62vh] overflow-y-auto">
            {sccContent ? (
              <pre className="mono text-[11.5px] leading-relaxed p-4 rounded-md whitespace-pre-wrap" style={{ background: "var(--bg-2)", color: "var(--ink)" }} data-testid="scc-content">{sccContent}</pre>
            ) : <Loader />}
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setSccModal(null)}>Close</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
